/**
 * The recurring schedule and collections.
 *
 * Only clients that actually recur appear — a priced plan plus a billing cycle
 * plus a next due date. Anything else is a data problem, and shows up in Needs
 * Action rather than here.
 */
import { useApp } from '../store/AppContext.jsx';
import { periodLabel, formatDate } from '../lib/dates.js';
import Icon from '../lib/icons.jsx';
import {
  Card, CardHead, EmptyState, Avatar, PlanPill, Pill
} from '../components/Primitives.jsx';
import { TileRow } from '../components/StatCards.jsx';
import { billingStats, collected } from '../domain/derive.js';
import { amountOf, isBilled } from '../domain/pricing.js';

/*
 * Parked on purpose: the page reports zeroes and an empty schedule.
 *
 * There is nothing to switch off fetch-side — Billing never loaded anything of
 * its own, it reads the client list the store already holds for every page. So
 * this gates the derivation instead. Flip to false to bring the real figures
 * back; nothing else has to change.
 */
const PAUSED = true;

const NO_TOTALS = { mrr: 0, arr: 0, dueMonth: 0, overdue: 0, got: 0, due30: 0, risk: 0 };

export default function Billing() {
  const { S, rows, derive, fmtMoney, markPaid, openDraft } = useApp();

  const b = PAUSED ? NO_TOTALS : billingStats(S, rows);

/*
 * Only accounts that actually buy something.
 *
 * The test used to be "has a plan, a cycle and a due date", which every free
 * signup passes: the connector hands each one a default plan and an expiry a
 * year out, and that expiry was being read as money owed. It put 229 accounts
 * on the schedule when 13 of them were customers, and not one of the other 216
 * had ever paid a dirham. acct_type is the connector saying plainly who is
 * buying, so ask that instead - and ask it rather than pay_status, so somebody
 * who has just signed up to pay still appears before their first payment.
 */
  const schedule = PAUSED ? [] : rows
    .filter(r => {
      const d = derive(r);
      return !d.excluded && d.live && d.type === 'Paying' && isBilled(S, r) && d.next;
    })
    .sort((a, c) => derive(a).next - derive(c).next);

  const tiles = [
    ['MRR', fmtMoney(b.mrr), 'zap', 'g-green'],
    ['ARR', fmtMoney(b.arr), 'chart', 'g-purple'],
    ['Due this month', fmtMoney(b.dueMonth), 'cal', 'g-blue'],
    ['Overdue', fmtMoney(b.overdue), 'alert', 'g-red'],
    ['Collected this month', fmtMoney(b.got), 'check', 'g-cyan'],
    ['At risk', fmtMoney(b.due30), 'card', 'g-amber']
  ];

  if (!schedule.length) {
    return (
      <>
        <TileRow tiles={tiles} />
        <Card>
          {PAUSED ? (
            <EmptyState icon="card" tone="g-blue" title="Billing is switched off">
              This page is not reading the book at the moment, so every total
              reads zero and the schedule is empty. Nothing has been deleted.
            </EmptyState>
          ) : (
            <EmptyState icon="card" tone="g-blue" title="No recurring subscriptions yet">
              Give a client a plan, a cycle and an amount and the schedule starts here.
            </EmptyState>
          )}
        </Card>
      </>
    );
  }

  return (
    <>
      <TileRow tiles={tiles} />

      <Card>
        <CardHead icon="refresh" tone="g-blue" title="Billing schedule" count={schedule.length} />

        <div className="tscroll">
          <table>
            <thead>
              <tr>
                <th>Client</th><th>Plan</th><th>Cycle</th><th>Amount</th>
                <th>Next due</th><th>Last paid</th><th>Collected</th><th />
              </tr>
            </thead>
            <tbody>
              {schedule.map(r => {
                const d = derive(r);
                const amount = amountOf(S, r);
                const tone = d.dueIn < 0 ? ' bad' : d.dueIn <= (S.dueSoonDays || 5) ? ' warn' : '';

                return (
                  <tr key={r.id}>
                    <td>
                      <div className="cname">
                        <Avatar name={r.name} />
                        <div><b>{r.name}</b><small>{r.handler || 'unowned'}</small></div>
                      </div>
                    </td>
                    <td><PlanPill value={r.plan} /></td>
                    <td>
                      <Pill tone={String(r.cycle).toLowerCase() === 'monthly' ? 'p-blue' : 'p-purple'} icon="refresh">
                        {r.cycle}
                      </Pill>
                    </td>
                    <td><b>{fmtMoney(amount)}</b><span className="dim">{periodLabel(r.cycle)}</span></td>
                    <td>
                      <span className={`datep${tone}`}>
                        <Icon name="cal" />
                        {formatDate(d.next, false, S.dateFmt)}
                        {d.dueIn < 0 ? ` · ${Math.abs(d.dueIn)}d late` : ` · in ${d.dueIn}d`}
                      </span>
                    </td>
                    <td className="dim">{r.lastPaid || '—'}</td>
                    <td className="dim">
                      {fmtMoney(collected(r))} <span style={{ fontSize: 11 }}>({(r.pays || []).length}×)</span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button type="button" className="btn sm primary" onClick={() => markPaid([r.id])}>
                          Mark paid
                        </button>
                        <button
                          type="button"
                          className="btn sm"
                          title="Invoice reminder"
                          onClick={() => openDraft({ kind: 'client', id: r.id })}
                        >
                          <Icon name="zap" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}
